/**
 * merchant-api（同一オリジンの `/api`）を叩く薄い層。
 *
 * 認証は HttpOnly cookie（`kb_session`）なので、この層はトークンを持たない・保存しない。
 * fetch の既定（same-origin）で cookie が付く。⚠️ 別オリジンの API を指す設定を作らない — 同一オリジンが
 * cookie・CSRF 対策（Origin 照合）の前提（`src/app.ts`）。
 */

export type NutritionKey = "cal" | "protein" | "fat" | "carbs" | "sugar" | "fiber" | "salt";
export type Nutrition = Record<NutritionKey, number | null>;

export interface Me {
  id: string;
  email: string;
  role: "owner" | "admin" | "staff" | string;
  merchant: { id: string; name: string };
  /** 運営から返信があって、まだ開いていないお問い合わせの数（画面上部のバッジ） */
  unreadFeedback: number;
  /** 利用規約（飲食ブランド向け補遺）。currentVersion = null は未公開（誰も申請できない） */
  terms: { currentVersion: string | null; acceptedVersion: string | null; accepted: boolean; url: string };
}

export interface LinkedChain {
  shopId: string;
  name: string;
  linkedAt: string;
  method: string;
}

/** 「公式値を希望する」の件数（`GET /catalog/official-requests`・無料）。1 件 = 1 人が 1 メニューに希望したこと */
export interface OfficialRequestSummary {
  chains: Array<{ shopId: string; requests: number; products: number; lastRequestedAt: string | null }>;
  top: Array<{ productId: number; shopId: string; name: string; requests: number; fulfilled: boolean }>;
}

export interface ChainCandidate {
  shopId: string;
  name: string;
  domain: string;
  state: "claimable" | "linked" | "needs_review";
}

export interface CatalogItem {
  productId: number;
  shopId: string;
  name: string;
  nameEn: string | null;
  price: number | null;
  nutrition: Nutrition;
  provenance: Record<NutritionKey, string | null>;
  nutritionSource: string | null;
  estimation: {
    reasoning: string | null;
    confidence: number | null;
    recognizedFood: string | null;
    components: unknown;
    analyzedAt: string | null;
  } | null;
  /** 誤っている可能性が高い品なら理由（null = 見つからない）。判定 = src/domain/catalog-view.ts の computeAttention */
  attention: { reasons: "kcal_pfc_mismatch"[]; pfcDerivedCal: number | null; score: number } | null;
  visibility: { app: true; web: string };
  sourcePageUrl: string | null;
  lastConfirmedAt: string | null;
  firstSeenDate: string | null;
  /** 一覧のときだけ付く: この商品に対する自社の台帳の行 */
  entry?: { id: string; status: string; submittedAt: string | null } | null;
  /** 利用者が「このお店に公式値を希望する」を押した件数（夜間の集計・0 = まだ無い）。src/domain/official-requests.ts */
  officialRequests?: number;
}

export type RecipeKey = NutritionKey;

export type AllergenState = "contains" | "may_contain" | "not_contained" | "unknown";
export interface AllergenStates {
  version: string;
  items: Record<string, AllergenState>;
}

export interface OwnIngredient {
  id: string;
  kind: "spec" | "part";
  name: string;
  maker: string | null;
  servingGrams: number | null;
  per100g: Record<RecipeKey, number | null>;
  recipe: { items: { id: string; grams: number }[]; finishedGrams: number | null } | null;
  allergenStates: AllergenStates | null;
  basisNote: string | null;
  updatedAt: string;
}

export interface MenuMatch {
  productId: number;
  shopId: string;
  name: string;
  currentCal: number | null;
  kind: "exact" | "partial";
}

export interface ChangeItem {
  productDbId: number | null;
  name: string;
  shopId: string | null;
  at: string;
  entry: { id: string; reviewState: string } | null;
}

export interface Changes {
  since: string;
  newWithoutOfficial: ChangeItem[];
  discontinued: ChangeItem[];
  nutritionChanged: ChangeItem[];
  pastAvailability: ChangeItem[];
  notifyWeekly: boolean;
}

export interface EvidenceDocument {
  id: string;
  kind: string;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  createdAt?: string;
}

export interface IngredientHit {
  id: string;
  name: string;
  category: string;
  /** master = 成分表 / own = 自社の食材・部品 */
  source: "master" | "own";
  per100g: Record<RecipeKey, number | null>;
  typicalUnits: { label: string; grams: number }[];
}

export interface RecipeCalc {
  servings: number;
  perServing: Record<RecipeKey, number | null>;
  undermeasured: RecipeKey[];
  unresolved: string[];
  totalGrams: number;
  perServingGrams: number;
  items: { id: string; name: string; grams: number }[];
  masterVersion: string;
  /** 100g あたり（仕上がり重量があればそれで割る） */
  per100g: Record<RecipeKey, number | null>;
  finishedGrams: number | null;
  /** 自社の食材・部品に付いたアレルゲン（候補） */
  allergenCandidates: Record<string, "contains" | "may_contain">;
  /** そのまま台帳（PUT の components）へ保存する形 */
  components: unknown[];
}

export interface PhotoEstimate {
  recognizedFood: string;
  confidence: number;
  reasoning: string;
  /** AI が直接出した 1 食の値（参考。材料に分けられなかったときだけ使う） */
  nutrition: Record<RecipeKey, number | null>;
  /** 成分表の ID に解決できた材料（1 食の重量） */
  items: { id: string; grams: number }[];
  notFood: boolean;
}

export type FeedbackStatus = "open" | "responded" | "closed";

export interface FeedbackThread {
  id: string;
  category: string;
  productDbId: number | null;
  productName: string | null;
  status: FeedbackStatus;
  /** 運営の返信をまだ開いていない */
  unread: boolean;
  lastMessage: { sender: "merchant" | "operator"; body: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackMessage {
  id: string;
  sender: "merchant" | "operator";
  /** 運営は「kalori 運営」、御社は担当者のメール */
  author: string;
  body: string;
  createdAt: string;
}

export type CatalogState = "listed" | "leaving" | "gone";

export type ReviewState = "editing" | "pending_review" | "published" | "unpublished" | "archived";

export interface MenuEntry {
  id: string;
  productDbId: number | null;
  name: string;
  nutrition: Nutrition;
  allergens: string[] | null;
  valueBasis: string | null;
  basisNote: string | null;
  /** 根拠の材料と重量（「材料から計算」で保存したもの）。⚠️ 配合は御社の機密 — kalori のカタログには出ない */
  components: unknown;
  /** 1 食の重量（g） */
  servingGrams: number | null;
  /** アレルゲンの 4 状態（品目リストの版つき） */
  allergenStates: AllergenStates | null;
  /** 提供期間（YYYY-MM-DD） */
  availableFrom: string | null;
  availableUntil: string | null;
  /** 直近の運営の判断（承認・差し戻し・公開停止）。差し戻しなら理由を画面に出す */
  lastDecision: { event: string; reason: string | null; reasonLabel: string | null; note: string | null; at: string | null } | null;
  status: string;
  submittedAt: string | null;
  reviewState: ReviewState;
  publishedAt: string | null;
  /**
   * 紐付け先がいま kalori に載っているか（null = 未紐付け）。
   * leaving = 公式サイトで見当たらなくなった（数日で外れる・戻ることもある）／ gone = 外れた（終売・改名）
   */
  catalogState: CatalogState | null;
  /** 付けた商品写真（承認されると kalori のカタログの画像になる）。url は同一オリジンの 400px 版 */
  photo: { id: string; url: string } | null;
  /** 1 件取得のときだけ: 改訂中（同じ商品に公開中の自社の値がある下書き）なら、いま公開している値 */
  revisionOf?: { id: string; nutrition: Nutrition; photo: { id: string; url: string } | null; publishedAt: string | null } | null;
  /** 1 件取得のときだけ: kalori がいまこの商品に出している画像（isAi = AI 生成・false = 承認済みの御社の写真） */
  catalogImage?: { url: string; isAi: boolean } | null;
  createdAt: string;
  updatedAt: string;
}

export interface Preview {
  nutrition: Record<string, number | null>;
  source: string | null;
  sourceMap: Record<string, string> | null;
  derived: { pfcDerivedCal: number };
}

export type SubmitWarning = "kcal_pfc_mismatch" | "kcal_not_verifiable" | "no_kcal";

export type ApiResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string; code?: string; body?: Record<string, unknown> };

const BASE = "/api";
/** 自社の公式値の書き出し（CSV）。リンクで開く（cookie が付く同一オリジン） */
export const EXPORT_CSV_URL = `${BASE}/export/entries.csv`;
/** 根拠資料の取り出し（ダウンロード） */
export const documentUrl = (id: string) => `${BASE}/documents/${encodeURIComponent(id)}`;

async function request<T>(method: string, path: string, body?: unknown): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, status: 0, error: "通信できませんでした。ネットワークを確認して、もう一度お試しください。" };
  }
  const json = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.ok && json?.success !== false) return { ok: true, status: res.status, data: json?.data as T };
  return {
    ok: false,
    status: res.status,
    error: typeof json?.error === "string" ? json.error : `エラーが発生しました（${res.status}）`,
    code: typeof json?.code === "string" ? json.code : undefined,
    body: json ?? undefined,
  };
}

export const api = {
  me: () => request<Me>("GET", "/auth/me"),
  sendCode: (email: string) => request<{ email: string }>("POST", "/auth/login", { email }),
  verifyCode: (email: string, code: string) => request<Me>("POST", "/auth/login/verify", { email, code }),
  logout: () => request<null>("POST", "/auth/logout"),
  acceptTerms: (version: string) => request<Me>("POST", "/auth/terms/accept", { version }),
  invite: (token: string) => request<{ email: string; merchantName: string }>("GET", `/auth/invite/${encodeURIComponent(token)}`),
  acceptInvite: (token: string) => request<Me>("POST", `/auth/invite/${encodeURIComponent(token)}`),

  chains: () => request<LinkedChain[]>("GET", "/claim"),
  candidates: () =>
    request<{ emailDomain: string | null; emailVerified: boolean; candidates: ChainCandidate[] }>("GET", "/claim/candidates"),
  claim: (shopIds: string[]) =>
    request<{ linked: string[]; alreadyLinked: string[]; needsReview: string[] }>("POST", "/claim/domain", { shopIds }),

  catalog: (params: { shopId?: string; q?: string; attention?: boolean; offset?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params.shopId) qs.set("shopId", params.shopId);
    if (params.q) qs.set("q", params.q);
    if (params.attention) qs.set("attention", "1");
    if (params.offset) qs.set("offset", String(params.offset));
    qs.set("limit", String(params.limit ?? 50));
    return request<{
      shopIds: string[];
      items: CatalogItem[];
      hasMore: boolean;
      /** 確認をおすすめで絞ったときだけ: 件数と、走査を打ち切ったか */
      attention: { total: number; truncated: boolean } | null;
      limit: number;
      offset: number;
    }>(
      "GET",
      `/catalog/products?${qs}`,
    );
  },
  catalogItem: (productId: number) => request<CatalogItem>("GET", `/catalog/products/${productId}`),
  officialRequests: () => request<OfficialRequestSummary>("GET", "/catalog/official-requests"),

  entries: () => request<MenuEntry[]>("GET", "/entries"),
  entry: (id: string) => request<MenuEntry>("GET", `/entries/${encodeURIComponent(id)}`),
  createEntry: (body: { name: string; nutrition?: Partial<Nutrition>; allergens?: string[] | null }) =>
    request<MenuEntry>("POST", "/entries", body),
  updateEntry: (
    id: string,
    body: {
      nutrition?: Partial<Nutrition>;
      allergens?: string[] | null;
      valueBasis?: string | null;
      basisNote?: string | null;
      components?: unknown[] | null;
      servingGrams?: number | null;
      allergenStates?: AllergenStates | null;
      availableFrom?: string | null;
      availableUntil?: string | null;
    },
  ) =>
    request<MenuEntry>("PUT", `/entries/${encodeURIComponent(id)}`, body),
  linkEntry: (id: string, productDbId: number) =>
    request<{ productDbId: number; productName: string; shopId: string }>("POST", `/entries/${encodeURIComponent(id)}/link`, { productDbId }),
  preview: (id: string) => request<Preview>("POST", `/entries/${encodeURIComponent(id)}/preview`),
  submit: (id: string) =>
    request<{ warnings: SubmitWarning[]; pfcDerivedCal: number | null }>("POST", `/entries/${encodeURIComponent(id)}/submit`),
  withdraw: (id: string) => request<null>("POST", `/entries/${encodeURIComponent(id)}/withdraw`),
  /** 公開中の値を改訂する下書きを作る（既にあればそれを返す） */
  reviseEntry: (id: string) => request<MenuEntry>("POST", `/entries/${encodeURIComponent(id)}/revise`),
  deleteEntry: (id: string) => request<null>("DELETE", `/entries/${encodeURIComponent(id)}`),

  /** 材料から計算（試用）。成分表の検索と、レシピ全体 → 1 食あたりの計算 */
  searchIngredients: (q: string) => request<IngredientHit[]>("GET", `/ingredients/search?q=${encodeURIComponent(q)}`),
  recipeCalc: (body: { name: string; servings: number; items: { id: string; grams: number }[]; finishedGrams?: number | null }) =>
    request<RecipeCalc>("POST", "/recipe/calc", body),
  /** レシピを別のメニュー（大盛・セット違い）へ写して下書きにする（値は写さない） */
  copyRecipe: (id: string, productDbId: number) =>
    request<MenuEntry>("POST", `/entries/${encodeURIComponent(id)}/copy-recipe`, { productDbId }),

  /** 自社の食材・部品（試用） */
  myIngredients: () => request<OwnIngredient[]>("GET", "/my-ingredients"),
  saveMyIngredient: (id: string | null, body: Record<string, unknown>) =>
    id ? request<OwnIngredient>("PUT", `/my-ingredients/${encodeURIComponent(id)}`, body) : request<OwnIngredient>("POST", "/my-ingredients", body),
  deleteMyIngredient: (id: string) => request<null>("DELETE", `/my-ingredients/${encodeURIComponent(id)}`),
  myIngredientUsage: (id: string) =>
    request<{ entries: { id: string; name: string; reviewState: string }[]; parts: { id: string; name: string }[] }>(
      "GET",
      `/my-ingredients/${encodeURIComponent(id)}/usage`,
    ),

  /** 一覧での一括入力 */
  bulkMatch: (names: string[]) => request<(MenuMatch | null)[]>("POST", "/bulk/match", { names }),
  bulkEntries: (body: {
    valueBasis: string;
    basisNote: string | null;
    rows: { productDbId: number; nutrition: Nutrition; servingGrams: number | null }[];
  }) => request<{ created: number; updated: number; skipped: { productDbId: number; reason: string }[] }>("POST", "/bulk/entries", body),
  bulkSubmit: (ids: string[]) =>
    request<{ submitted: number; results: { id: string; ok: boolean; blockers: string[]; warnings: string[] }[] }>("POST", "/bulk/submit", { ids }),

  /** 根拠資料（AI に送らない・保管するだけ） */
  documents: (entryId: string) => request<EvidenceDocument[]>("GET", `/documents?entryId=${encodeURIComponent(entryId)}`),
  uploadDocument: async (entryId: string, kind: string, file: File): Promise<ApiResult<EvidenceDocument>> => {
    const form = new FormData();
    form.set("entryId", entryId);
    form.set("kind", kind);
    form.set("file", file);
    try {
      const res = await fetch(`${BASE}/documents`, { method: "POST", body: form, credentials: "same-origin" });
      const json = (await res.json().catch(() => null)) as { success?: boolean; data?: EvidenceDocument; error?: string } | null;
      if (res.ok && json?.success !== false) return { ok: true, status: res.status, data: json?.data as EvidenceDocument };
      return { ok: false, status: res.status, error: json?.error ?? `エラーが発生しました（${res.status}）` };
    } catch {
      return { ok: false, status: 0, error: "通信できませんでした。ネットワークを確認して、もう一度お試しください。" };
    }
  },
  deleteDocument: (id: string) => request<null>("DELETE", `/documents/${encodeURIComponent(id)}`),

  // 商品写真（公式値の行に付ける・承認でカタログの画像になる）
  uploadPhoto: (body: { entryId: string; imageBase64: string; rightsConfirmed: true }) =>
    request<{ id: string; url: string }>("POST", "/photos", body),
  removePhoto: (entryId: string) => request<null>("DELETE", `/photos/entries/${encodeURIComponent(entryId)}`),

  /** 変化の知らせ（試用） */
  changes: (days = 30) => request<Changes>("GET", `/changes?days=${days}`),
  setWeeklyNotify: (weekly: boolean) => request<{ notifyWeekly: boolean }>("PUT", "/changes/notifications", { weekly }),

  /** 写真から推定（試用）。⚠️ consent = 写真を外部の AI（Google Gemini）へ送ることへの同意。写真は保存されない */
  photoEstimate: (body: { name: string; hint: string | null; imageBase64: string; consent: true }) =>
    request<PhotoEstimate>("POST", "/recipe/photo-estimate", body),

  /** お問い合わせ（スレッド）。担当者・会社名はサーバがセッションから付ける */
  createFeedback: (body: { category: string; message: string; page: string | null; productDbId: number | null }) =>
    request<{ id: string }>("POST", "/feedback", body),
  feedbackList: () => request<FeedbackThread[]>("GET", "/feedback"),
  feedbackThread: (id: string) =>
    request<FeedbackThread & { page: string | null; messages: FeedbackMessage[] }>("GET", `/feedback/${encodeURIComponent(id)}`),
  /** 画面に出した最後の発言（upTo）までを既読にする */
  readFeedback: (id: string, upTo: string) => request<null>("POST", `/feedback/${encodeURIComponent(id)}/read`, { upTo }),
  replyFeedback: (id: string, message: string) =>
    request<null>("POST", `/feedback/${encodeURIComponent(id)}/messages`, { message }),
};
